import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {
  Button,
  Alert,
  Collapse,
  Typography,
  List,
  ListItem,
  Switch,
  FormControlLabel,
  Link,
  Divider,
} from '@mui/material';
import { useState } from 'react';
import emoji from '../assets/download-emoji.png';
import { useDatasetDescriptionFormValidation } from '../hooks/useDatasetDescriptionFormValidation';
import { useGenerateDataDictionary } from '../hooks/useGenerateDataDictionary';
import { useGenerateDatasetDescription } from '../hooks/useGenerateDatasetDescription';
import { usePageAlerts } from '../hooks/usePageAlerts';
import { useParticipantIdStatus } from '../hooks/useParticipantIdStatus';
import { useSchemaValidation } from '../hooks/useSchemaValidation';
import { useDataActions, useUploadedDataTableFileName, useConfig } from '../stores/data';
import useViewStore from '../stores/view';
import { View } from '../utils/internal_types';
import DataDictionaryPreview from './DataDictionaryPreview';
import GoogleDriveUpload from './GoogleDriveUpload';
import PageAlert from './PageAlert';

function Download() {
  const [dictionaryCollapsed, setDictionaryCollapsed] = useState(false);
  const [forceAllowDownload, setForceAllowDownload] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const uploadedDataTableFileName = useUploadedDataTableFileName();
  const config = useConfig();
  const { reset } = useDataActions();
  const setCurrentView = useViewStore((state) => state.setCurrentView);

  const dataDictionary = useGenerateDataDictionary();
  const datasetDescription = useGenerateDatasetDescription();
  const { isFormInvalid } = useDatasetDescriptionFormValidation();
  const { schemaValid, schemaErrors } = useSchemaValidation(dataDictionary);
  const { hasParticipantIdMissingValues } = useParticipantIdStatus();
  const alerts = usePageAlerts(View.Download);
  const isParticipantIdValid = !hasParticipantIdMissingValues;
  const isDownloadReady = schemaValid && isParticipantIdValid;

  const handleDownloadDataDictionary = () => {
    const dataDictionaryBlob = new Blob([JSON.stringify(dataDictionary, null, 2)], {
      type: 'application/json;charset=utf-8',
    });

    const dataDictionaryUrl = URL.createObjectURL(dataDictionaryBlob);
    const dataDictionaryAnchor = document.createElement('a');
    dataDictionaryAnchor.href = dataDictionaryUrl;
    dataDictionaryAnchor.download = `${uploadedDataTableFileName?.slice(0, -4)}_annotated.json`;
    document.body.appendChild(dataDictionaryAnchor);
    dataDictionaryAnchor.click();
    URL.revokeObjectURL(dataDictionaryUrl);
    document.body.removeChild(dataDictionaryAnchor);
  };

  const handleDownloadDatasetDescription = () => {
    if (datasetDescription) {
      const datasetDescriptionBlob = new Blob([JSON.stringify(datasetDescription, null, 2)], {
        type: 'application/json;charset=utf-8',
      });
      const datasetDescriptionUrl = URL.createObjectURL(datasetDescriptionBlob);
      const datasetDescriptionAnchor = document.createElement('a');
      datasetDescriptionAnchor.href = datasetDescriptionUrl;
      datasetDescriptionAnchor.download = `${uploadedDataTableFileName?.slice(0, -4) || 'dataset'}_dataset_description.json`;
      document.body.appendChild(datasetDescriptionAnchor);
      datasetDescriptionAnchor.click();
      URL.revokeObjectURL(datasetDescriptionUrl);
      document.body.removeChild(datasetDescriptionAnchor);
    }
  };

  const handleAnnotatingNewDataset = () => {
    reset();
    setCurrentView(View.Upload);
  };

  return (
    <div className="flex flex-col items-center p-6" data-cy="download">
      {alerts.map((alert) => (
        <PageAlert key={alert.id} {...alert} className="mb-6 w-full max-w-2xl" />
      ))}

      {!schemaValid && (
        <Alert
          severity="warning"
          data-cy="incomplete-annotations-alert"
          className="mb-6 w-full max-w-2xl"
        >
          <Typography variant="h6" className="mb-2 font-bold">
            Incomplete Annotations
          </Typography>
          <Typography variant="body1" className="mb-2">
            There are incomplete annotations for columns that were mapped to variables. You can go
            back and complete these annotations before you save the .json data dictionary.
          </Typography>
          <Typography variant="body1" className="mb-2">
            The following columns have incomplete value annotations:
          </Typography>
          <List className="list-disc pl-6" data-cy="incomplete-annotations-list">
            {schemaErrors.map((column) => (
              <ListItem key={column} className="list-item">
                <Typography variant="body1">{column}</Typography>
              </ListItem>
            ))}
          </List>
          <Typography variant="body1" className="mb-2">
            <b>NOTE</b>: If you save the .json data dictionary without completing the annotations,
            the data dictionary will not work as-is with other Neurobagel tools. However, you can
            save and reload the partially annotated .json to complete annotations later, if desired.
          </Typography>
        </Alert>
      )}

      {isDownloadReady && (
        <Alert
          data-cy="complete-annotations-alert"
          severity="success"
          className="mb-6 w-full max-w-2xl"
        >
          <div className="flex items-center gap-2">
            <img src={emoji} alt="bagel confetti" className="h-10 w-10" />
            <Typography variant="h4" className="font-bold">
              Congratulations!
            </Typography>
          </div>
          <Typography variant="body1">
            You have successfully created a{' '}
            <Link
              href="https://neurobagel.org/data_models/dictionaries/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              neurobagel annotated .json data dictionary
            </Link>
            .
          </Typography>
        </Alert>
      )}

      <div className="w-full max-w-2xl">
        <Typography variant="h5" className="mb-2 font-bold">
          Data Dictionary
        </Typography>
        <Typography variant="body1" className="mb-4">
          Here is the final .json data dictionary that you have created:
        </Typography>

        <Button
          data-cy="datadictionary-toggle-preview-button"
          variant="outlined"
          size="small"
          onClick={() => setDictionaryCollapsed(!dictionaryCollapsed)}
          className="mb-4"
        >
          {dictionaryCollapsed ? 'Show' : 'Hide'} Data Dictionary
        </Button>

        <Collapse in={!dictionaryCollapsed}>
          <DataDictionaryPreview dataDictionary={dataDictionary} />
        </Collapse>

        <Typography variant="h6" className="font-bold mt-4">
          Here are some next steps:
        </Typography>
        <List className="list-disc pl-6" data-cy="datadictionary-next-steps-list">
          <ListItem className="list-item">
            <Typography variant="body1">
              Save the .json data dictionary to your local hard drive by clicking the green
              &quot;Save&quot; button.
            </Typography>
          </ListItem>
          <ListItem className="list-item">
            <Typography variant="body1">
              Use the .json data dictionary to{' '}
              <Link
                href="https://neurobagel.org/user_guide/cli/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                create a harmonized view of your data
              </Link>
              .
            </Typography>
          </ListItem>
          <ListItem className="list-item">
            <Typography variant="body1">
              Learn more about{' '}
              <Link
                href="https://neurobagel.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                neurobagel
              </Link>{' '}
              and how to use harmonized data for cross-dataset search.
            </Typography>
          </ListItem>
        </List>
      </div>

      <div className="flex w-full flex-col items-center">
        {/* ENIGMA-PD Specific Upload Section */}
        {config === 'ENIGMA-PD' && (
          <>
            <Divider className="my-6 w-full max-w-2xl" />
            <div
              className="my-2 flex w-full max-w-2xl flex-col items-center"
              data-cy="upload-section"
            >
              <Typography
                variant="h5"
                className="mb-2 w-full font-bold"
                data-cy="upload-section-header"
              >
                Upload this data dictionary to the {config} drive
              </Typography>
              <Alert
                severity="info"
                className="mb-2 w-full"
                variant="filled"
                data-cy="upload-info-alert"
                sx={{
                  bgcolor: '#e5f6fd', // light cyan background
                  borderLeft: '4px solid #0288d1', // info blue border
                  color: 'rgba(0, 0, 0, 0.87)', // dark text for readability
                  '& .MuiAlert-icon': {
                    color: '#0288d1',
                  },
                }}
              >
                <Typography variant="subtitle2">
                  The data dictionary and dataset description (if complete) will be uploaded to the
                  ENIGMA-PD community drive
                </Typography>
                <Typography variant="subtitle2">Your tabular data remains local.</Typography>
              </Alert>

              <Button
                variant="contained"
                color="primary"
                endIcon={<CloudUploadIcon />}
                onClick={() => setUploadDialogOpen(true)}
                disabled={!isDownloadReady && !forceAllowDownload}
                className="mt-4"
                data-cy="upload-drive-button"
              >
                Upload Dataset Information to {config}
              </Button>
            </div>
            <Divider className="my-6 w-full max-w-2xl" />
          </>
        )}

        {!isDownloadReady ? (
          <FormControlLabel
            control={
              <Switch
                data-cy="force-download-switch"
                checked={forceAllowDownload}
                onChange={(e) => setForceAllowDownload(e.target.checked)}
              />
            }
            label="Let me save, I know what I'm doing!"
          />
        ) : null}

        {isFormInvalid && (
          <Alert
            severity="info"
            className="mb-4 w-full max-w-2xl"
            data-cy="dataset-description-invalid-alert"
          >
            <Typography variant="body2">
              The Dataset Description form is incomplete. Only the Data Dictionary can be saved.
            </Typography>
          </Alert>
        )}

        <div className="flex gap-4">
          <Button
            data-cy="download-datadictionary-button"
            variant="contained"
            color={isDownloadReady ? 'success' : 'warning'}
            disabled={!isDownloadReady && !forceAllowDownload}
            onClick={handleDownloadDataDictionary}
            endIcon={<SaveAltIcon />}
          >
            Save Data Dictionary
          </Button>
          {!isFormInvalid && (
            <Button
              data-cy="download-datasetdescription-button"
              variant="contained"
              color="success"
              onClick={handleDownloadDatasetDescription}
              endIcon={<SaveAltIcon />}
            >
              Save Dataset Description
            </Button>
          )}
        </div>
      </div>

      <Button
        data-cy="annotate-new-dataset-button"
        variant="contained"
        color="info"
        onClick={handleAnnotatingNewDataset}
        className="mt-4"
        endIcon={<NoteAddIcon />}
      >
        Annotate a new dataset
      </Button>

      <GoogleDriveUpload
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        dataDictionary={dataDictionary}
        datasetDescription={datasetDescription}
        config={config}
      />
    </div>
  );
}

export default Download;
